"use client"
import { CalendarIcon, TagIcon, UserIcon } from "@heroicons/react/24/outline"
import Link from "next/link"
import Image from "next/image"
import NoSsr from "~/app/_components/common/noSsr"
import { absoluteUrl, formatDate } from "~/app/utils/helper"
import { Insights } from "~/content/navLinks"
import type { JsonApiResource, DrupalNode } from "next-drupal"
import parse, { Element, type HTMLReactParserOptions, domToReact, type DOMNode } from "html-react-parser"
import type { FilterTagWithType } from "../pageSpecific/insight/tagFilter"
import ArticleGridSection from "../pageSpecific/insight/articleGridSection"

type NodeArticleProps = {
  node: DrupalNode
  articles?: JsonApiResource[] | null
}

const parseOptions: HTMLReactParserOptions = {
  replace(domNode) {
    if (domNode instanceof Element) {
      // replace images with Next/Image
      if (domNode.name === "img") {
        const { src, alt, width, height } = domNode.attribs as {
          src: string
          alt: string
          width: string
          height: string
        }
        return (
          <Image
            src={absoluteUrl(`/${src}`)}
            width={parseInt(width)}
            height={parseInt(height)}
            className="object-cover"
            alt={alt}
          />
        )
      }
      // replace links with Next/Link
      // sometimes Drupal creates links with no href which causes Nextjs to crash; strip these
      if (domNode.name === "a") {
        const { href, class: className } = domNode.attribs as {
          href: string
          class: string
        }
        // add type DOMNode[] to pass build --- domNode error
        if (!href) {
          return <>{domToReact(domNode.children as DOMNode[])}</>
        }
        return (
          <Link href={href} className={className}>
            {domToReact(domNode.children as DOMNode[])}
          </Link>
        )
      }
      // replace embeds with ... a proper embed
      if (domNode.name === "iframe" && domNode.attribs.class?.includes("media-oembed-content")) {
        const { src, title } = domNode.attribs as {
          src: string
          title: string
        }

        // because for some reason %3F doesn't get decoded?
        //decodeURI(src.replace('/media/oembed?url=https%3A//', 'https://').replace('%3F', '?'))
        const newSrc = decodeURI(src)
        const commonClass = "w-full max-w-3xl mx-auto aspect-video"

        if (src.includes("youtu")) {
          return (
            <iframe
              src={newSrc} //.replace(/&max_width=0&max_height=0&hash=.*/, '')}
              className={`${commonClass} rounded-xl`}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          )
        } else if (src.includes("vimeo")) {
          return (
            <iframe
              src={newSrc}
              className={`${commonClass} rounded-xl`}
              title={title}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          )
        }
        // shouldn't get here
        return <iframe src={newSrc} className={`${commonClass}`} title={title} />
      }
    }
  },
}

const NodeArticle = ({ node, articles }: NodeArticleProps) => {
  const author = (node.field_displayed_author?.name as string) || "Incluso Team"
  const body = node.body?.processed ? (parse(node.body.processed, parseOptions) as string) : undefined
  const allTags: FilterTagWithType[] = [
    ...node.field_tags?.map((tag: { name: string }) => ({ type: "tag", tag: tag.name }) as const),
  ]

  return (
    <>
      <main className="w-full bg-gradient-blue1">
        <div className={`relative flex`}>
          <div
            className={`mx-auto w-full px-6 py-24 max:container max:relative max:left-1/2 max:mx-0 max:-translate-x-1/2`}
          >
            <article className="mx-auto max-w-3xl leading-7 text-gray-300">
              <h1 className="text-resp-4xl mt-2 font-bold tracking-tight text-white sm:text-4xl">{node.title}</h1>
              <NoSsr>
                <div className="mt-3 flex items-center gap-2 text-base leading-relaxed text-gray-200">
                  <CalendarIcon height={24} className="mr-2" /> {formatDate(node.created)}
                </div>
              </NoSsr>
              {author && (
                <div className="mt-1 flex items-center gap-2 text-base leading-relaxed text-gray-200">
                  <UserIcon height={24} className="mr-2" /> {author}
                </div>
              )}
              {allTags && allTags.length !== 0 && (
                <div className="mt-1 flex flex-wrap items-center gap-y-2 text-sm">
                  <TagIcon height={24} className="" />
                  {allTags?.slice(0, 3).map((tag, i: number) => (
                    <Link
                      key={i}
                      href={`${Insights.href}?${tag.type}=${tag.tag}`}
                      className="relative z-10 ml-2 rounded-full bg-white/10 px-3 py-1.5 font-semibold text-green hover:bg-white/20"
                    >
                      {tag.tag}
                    </Link>
                  ))}
                </div>
              )}

              {node.field_image && (
                <figure className="relative mt-8">
                  <Image
                    src={absoluteUrl(node.field_image.uri.url as string)}
                    width={768}
                    height={400}
                    alt={(node.field_image.resourceIdObjMeta.alt as string) ?? "blog-image"}
                    priority
                    className="h-auto max-h-[400px] w-full max-w-[768px] rounded-2xl bg-gray-900 object-cover"
                  />
                  <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/20" />
                  {node.field_image.resourceIdObjMeta.title && (
                    <figcaption className="py-2 text-center text-sm text-gray-300">
                      {node.field_image.resourceIdObjMeta.title}
                    </figcaption>
                  )}
                </figure>
              )}

              <NoSsr>
                {body && (
                  <div className="prose prose-invert mr-0 mt-6 w-full max-w-none text-base leading-7 prose-h2:text-green prose-h3:text-green prose-h4:text-green prose-h5:text-green prose-h6:text-green prose-a:text-green prose-a:no-underline hover:prose-a:text-green prose-blockquote:border-l-2 prose-blockquote:border-green prose-blockquote:text-green prose-img:rounded-xl">
                    {body}
                  </div>
                )}
                <div id="hubspotForm" />
              </NoSsr>
            </article>
          </div>
        </div>

        {/* Related Posts */}
        <ArticleGridSection
          title="Related posts"
          {...{ articles }}
          filterTags={allTags}
          currentArticleId={node.id}
          minShow={1}
        />
      </main>
    </>
  )
}

export default NodeArticle
